import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import EditorialSubNav from '../components/EditorialSubNav';
import { useT } from '../i18n/LanguageContext';

const eyebrowStyle = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '9pt',
  fontWeight: 600,
  letterSpacing: '0.32em',
  textTransform: 'uppercase',
  color: '#D6A87A',
};

const SectionEyebrow = ({ children, color = '#D6A87A' }) => (
  <div style={{ ...eyebrowStyle, color }} className="mb-6">
    {children}
  </div>
);

const Mice = () => {
  const t = useT();
  const subNavLinks = [
    { label: t('subnav.approach'), target: 'approach' },
    { label: t('subnav.method'), target: 'method' },
    { label: t('subnav.capabilities'), target: 'capabilities' },
    { label: t('subnav.congresses'), target: 'cases' },
    { label: t('subnav.studio'), target: 'studio' },
    { label: t('subnav.contact'), target: 'contact' },
  ];
  const method = [
    { no: '01', titleKey: 'mice.method.01.title', descKey: 'mice.method.01.desc' },
    { no: '02', titleKey: 'mice.method.02.title', descKey: 'mice.method.02.desc' },
    { no: '03', titleKey: 'mice.method.03.title', descKey: 'mice.method.03.desc' },
    { no: '04', titleKey: 'mice.method.04.title', descKey: 'mice.method.04.desc' },
    { no: '05', titleKey: 'mice.method.05.title', descKey: 'mice.method.05.desc' },
  ];

  const capabilities = [
    { titleKey: 'mice.capabilities.capacity', lines: t('mice.capabilities.capacityLines') || [] },
    { titleKey: 'mice.capabilities.production', lines: t('mice.capabilities.productionLines') || [] },
    { titleKey: 'mice.capabilities.logistics', lines: t('mice.capabilities.logisticsLines') || [] },
  ];

  const sigMedia = [
    { venue: 'MANDARIN ORIENTAL', image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&q=80' },
    { venue: 'MESSE STUTTGART', image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=1200&q=80' },
    { venue: 'MUSLIM HANDS UK', image: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1200&q=80' },
  ];
  const sigCopy = t('mice.signature.items') || [];
  const signature = sigMedia.map((s, i) => ({ ...s, ...(sigCopy[i] || {}) }));

  return (
    <div className="bg-navy text-bone min-h-screen" data-testid="mice-page">
      <SEO
        title="MICE, Conferences, Congresses & Incentives | GlowMeets"
        description="Premium MICE planning across Türkiye. Conferences from 80 to 1,500 delegates, full AV production, multi-language interpretation, and on-site delivery teams."
        keywords="MICE Turkey, conference Istanbul, congress Antalya, incentive group Turkey, corporate retreat Bodrum, exhibition organizer Istanbul"
        url="/mice"
        service={{
          name: 'GlowMeets MICE',
          serviceType: 'Meetings, Incentives, Conferences and Exhibitions',
          description:
            'End-to-end MICE planning across Türkiye. Conferences, congresses, incentive groups and corporate retreats from 80 to 1,500 delegates.',
        }}
      />

      {/* ───────────────────────── 1. SUB-HERO ───────────────────────── */}
      <section
        className="relative w-full overflow-hidden"
        style={{ minHeight: '88vh' }}
        data-testid="mice-subhero"
      >
        <img
          src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=2200&q=80"
          alt="Conference auditorium with delegates"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Navy gradient overlay, emphasised at bottom 40% */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(10,19,47,0.55) 0%, rgba(10,19,47,0.35) 35%, rgba(10,19,47,0.7) 60%, rgba(10,19,47,0.92) 100%)',
          }}
        />

        <div
          className="relative z-10 max-w-container mx-auto px-4 sm:px-6 lg:px-8 flex items-center"
          style={{ minHeight: '88vh', paddingTop: '120px', paddingBottom: '90px' }}
        >
          <div className="max-w-[58ch]">
            <SectionEyebrow>{t('mice.hero.eyebrow')}</SectionEyebrow>

            <h1
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(40px, 5vw, 64pt)',
                lineHeight: 1.05,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              <span className="block">{t('mice.hero.line1')}</span>
              <span className="block">{t('mice.hero.line2')}</span>
              <span
                className="block italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('mice.hero.line3')}
              </span>
            </h1>

            <p
              className="mt-8"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '18pt',
                color: 'rgba(244,239,227,0.82)',
                lineHeight: 1.5,
                maxWidth: '60ch',
              }}
            >
              {t('mice.hero.sub')}
            </p>

            <div
              className="mt-10"
              style={{
                height: '1px',
                width: '88px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* Sticky in-page sub-nav */}
      <EditorialSubNav links={subNavLinks} surface="navy" />

      {/* ───────────────────────── 2. THE APPROACH ───────────────────────── */}
      <section
        id="approach"
        className="bg-bone text-ink"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="mice-approach"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[60ch]">
            <SectionEyebrow>{t('mice.approach.eyebrow')}</SectionEyebrow>
            <h2
              className="text-ink"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(28px, 2.8vw, 36pt)',
                lineHeight: 1.15,
                fontWeight: 500,
                letterSpacing: '-0.005em',
              }}
            >
              {t('mice.approach.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#B98556', fontWeight: 400 }}
              >
                {t('mice.approach.headline2')}
              </em>
            </h2>

            <div
              className="mt-10 space-y-6"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13pt',
                fontWeight: 400,
                lineHeight: 1.7,
                color: '#4A4F63',
              }}
            >
              {(t('mice.approach.body') || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            <div
              className="mt-12"
              style={{
                height: '1px',
                width: '64px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ───────────────────────── 3. THE METHOD ───────────────────────── */}
      <section
        id="method"
        className="bg-navy"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="mice-method"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <SectionEyebrow>{t('mice.method.eyebrow')}</SectionEyebrow>
            <h2
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(30px, 3vw, 44px)',
                lineHeight: 1.1,
                fontWeight: 500,
              }}
            >
              {t('mice.method.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('mice.method.headline2')}
              </em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-0">
            {method.map((step, i) => (
              <div
                key={step.no}
                className="relative px-6 py-8"
                style={{
                  borderTop: '1px solid rgba(214,168,122,0.45)',
                  borderRight:
                    i < method.length - 1
                      ? '1px solid rgba(214,168,122,0.18)'
                      : 'none',
                }}
                data-testid={`mice-method-${step.no}`}
              >
                <div
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: '64pt',
                    lineHeight: 1,
                    fontWeight: 400,
                    fontStyle: 'italic',
                    color: '#D6A87A',
                  }}
                >
                  {step.no}
                </div>

                <div
                  className="mt-6"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11pt',
                    fontWeight: 600,
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: '#D6A87A',
                  }}
                >
                  {t(step.titleKey)}
                </div>

                <p
                  className="mt-3"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '11pt',
                    fontWeight: 400,
                    lineHeight: 1.6,
                    color: 'rgba(244,239,227,0.82)',
                  }}
                >
                  {t(step.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 4. CAPACITY & CAPABILITIES ───────────────────────── */}
      <section
        id="capabilities"
        className="bg-navy-deep"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="mice-capabilities"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <SectionEyebrow>{t('mice.capabilities.eyebrow')}</SectionEyebrow>
            <h2
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontSize: '28pt',
                lineHeight: 1.1,
                fontWeight: 400,
                color: '#D6A87A',
                letterSpacing: '-0.005em',
              }}
            >
              {t('mice.capabilities.headline')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-12">
            {capabilities.map((c, idx) => (
              <div key={c.titleKey} data-testid={`mice-capability-${idx + 1}`}>
                <div
                  style={{
                    height: '1px',
                    width: '40px',
                    backgroundColor: 'rgba(214,168,122,0.55)',
                  }}
                  className="mb-5"
                />
                <h3
                  style={{
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: '18pt',
                    fontWeight: 500,
                    lineHeight: 1.2,
                    color: '#D6A87A',
                    letterSpacing: '-0.005em',
                  }}
                  className="mb-5"
                >
                  {t(c.titleKey)}
                </h3>
                <ul className="space-y-3">
                  {c.lines.map((line) => (
                    <li
                      key={line}
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '10pt',
                        fontWeight: 400,
                        lineHeight: 1.6,
                        color: 'rgba(244,239,227,0.82)',
                      }}
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 5. SIGNATURE CONGRESSES ───────────────────────── */}
      <section
        id="cases"
        className="bg-navy"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="mice-signature"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <SectionEyebrow>{t('mice.signature.eyebrow')}</SectionEyebrow>
            <h2
              className="text-bone"
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: 'clamp(30px, 3vw, 44px)',
                lineHeight: 1.1,
                fontWeight: 500,
              }}
            >
              {t('mice.signature.headline1')}{' '}
              <em
                className="italic"
                style={{ color: '#D6A87A', fontWeight: 400 }}
              >
                {t('mice.signature.headline2')}
              </em>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-12">
            {signature.map((c, index) => (
              <article
                key={c.venue}
                className="group"
                data-testid={`mice-case-${index + 1}`}
              >
                <div
                  className="relative overflow-hidden"
                  style={{ aspectRatio: '4/5' }}
                >
                  <img
                    src={c.image}
                    alt={c.title}
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                  />
                  {/* Navy bottom overlay */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        'linear-gradient(to bottom, rgba(10,19,47,0) 60%, rgba(10,19,47,0.85) 100%)',
                    }}
                  />
                  {/* Copper category chip */}
                  <div
                    className="absolute top-5 left-5"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: '#D6A87A',
                      border: '1px solid rgba(214,168,122,0.6)',
                      padding: '6px 12px',
                      backgroundColor: 'rgba(10,19,47,0.45)',
                    }}
                  >
                    {t('filters.mice')}
                  </div>
                </div>

                <div className="mt-6">
                  <div
                    className="mb-3"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: 'rgba(214,168,122,0.78)',
                    }}
                  >
                    {t('mice.signature.kind')} · {c.venue}
                  </div>

                  <h3
                    style={{
                      fontFamily: '"Cormorant Garamond", serif',
                      fontSize: '22pt',
                      fontWeight: 500,
                      lineHeight: 1.2,
                      color: '#D6A87A',
                      letterSpacing: '-0.005em',
                    }}
                  >
                    {c.title}
                  </h3>

                  <p
                    className="mt-3"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '10pt',
                      fontWeight: 400,
                      lineHeight: 1.65,
                      color: 'rgba(244,239,227,0.82)',
                    }}
                  >
                    {c.summary}
                  </p>

                  <div
                    className="mt-5"
                    style={{
                      height: '1px',
                      width: '32px',
                      backgroundColor: 'rgba(214,168,122,0.55)',
                    }}
                  />

                  <div
                    className="mt-4"
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '9pt',
                      fontWeight: 600,
                      letterSpacing: '0.32em',
                      textTransform: 'uppercase',
                      color: '#D6A87A',
                    }}
                  >
                    {t('common.outcome')}, {c.outcome}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────────── 6. THE STUDIO ───────────────────────── */}
      <section
        id="studio"
        className="bg-bone text-ink"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="mice-studio"
      >
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-[70ch] mx-auto">
            <SectionEyebrow>{t('mice.studio.eyebrow')}</SectionEyebrow>

            <p
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontStyle: 'italic',
                fontWeight: 400,
                fontSize: '16pt',
                lineHeight: 1.55,
                color: '#0A132F',
                letterSpacing: '-0.002em',
              }}
            >
              {t('mice.studio.body')}
            </p>

            <div
              className="mt-10"
              style={{
                height: '1px',
                width: '64px',
                backgroundColor: 'rgba(214,168,122,0.55)',
              }}
            />
          </div>
        </div>
      </section>

      {/* ───────────────────────── 7. SECTION CTA ───────────────────────── */}
      <section
        id="contact"
        className="bg-navy text-bone relative overflow-hidden"
        style={{ paddingTop: '90px', paddingBottom: '90px' }}
        data-testid="mice-cta"
      >
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2"
          style={{
            height: '1px',
            width: '160px',
            backgroundColor: 'rgba(214,168,122,0.55)',
          }}
        />

        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionEyebrow>{t('mice.cta.eyebrow')}</SectionEyebrow>

          <h2
            className="text-bone"
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: 'clamp(36px, 4vw, 54px)',
              lineHeight: 1.05,
              fontWeight: 500,
            }}
          >
            {t('mice.cta.headline1')}{' '}
            <em
              className="italic"
              style={{ color: '#D6A87A', fontWeight: 400 }}
            >
              {t('mice.cta.headline2')}
            </em>
          </h2>

          <div className="mt-12 flex flex-col items-center gap-5">
            {/* Primary, copper fill */}
            <Link
              to="/contact"
              data-testid="mice-cta-primary"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#0A132F',
                backgroundColor: '#D6A87A',
                border: '1px solid #D6A87A',
                padding: '14px 32px',
                borderRadius: '999px',
                minWidth: '320px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#B98556';
                e.currentTarget.style.borderColor = '#B98556';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.borderColor = '#D6A87A';
              }}
            >
              {t('cta.bookConsultation')}
            </Link>

            {/* Secondary, copper outline */}
            <Link
              to="/mice/capabilities"
              data-testid="mice-cta-secondary"
              className="inline-flex items-center justify-center transition-colors"
              style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '11pt',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: '#D6A87A',
                backgroundColor: 'transparent',
                border: '1px solid #D6A87A',
                padding: '14px 32px',
                borderRadius: '999px',
                minWidth: '320px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#D6A87A';
                e.currentTarget.style.color = '#0A132F';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#D6A87A';
              }}
            >
              {t('cta.downloadMiceDeck')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mice;
